import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Plus, ShieldAlert, ArrowLeft, FileText, Search, Eye, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  SafetyTemplate,
  STATUS_LABEL,
  STATUS_VARIANT,
  TYPE_LABEL,
  TARGET_GROUP_LABEL,
} from "./types";
import { SafetyTemplateEditor } from "./SafetyTemplateEditor";
import { SafetyTemplateDetail } from "./SafetyTemplateDetail";
import { SafetyAuditLog } from "./SafetyAuditLog";
import { SafetyAssignmentsManager } from "./SafetyAssignmentsManager";

export const SafetyInstructionsSettings = () => {
  const { userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const [templates, setTemplates] = useState<SafetyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<SafetyTemplate | null>(null);
  const [selected, setSelected] = useState<SafetyTemplate | null>(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("templates");

  const fetchTemplates = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("safety_instruction_templates")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Fehler beim Laden der Vorlagen");
    } else {
      setTemplates((data as unknown as SafetyTemplate[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filtered = templates.filter((t) =>
    t.title.toLowerCase().includes(search.toLowerCase())
  );

  if (selected) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => { setSelected(null); fetchTemplates(); }} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </Button>
        <SafetyTemplateDetail
          template={selected}
          isAdmin={isAdmin}
          onReload={fetchTemplates}
          onTemplateChange={(t) => setSelected(t)}
        />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-primary" />
          Sicherheitsunterweisungen
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="templates">Vorlagen</TabsTrigger>
            <TabsTrigger value="assignments">Zuweisungen</TabsTrigger>
            <TabsTrigger value="audit">Audit-Log</TabsTrigger>
          </TabsList>

          <TabsContent value="assignments" className="mt-4">
            <SafetyAssignmentsManager />
          </TabsContent>

          <TabsContent value="templates" className="space-y-4 mt-4">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Vorlagen suchen..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Button
                onClick={() => { setEditing(null); setEditorOpen(true); }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" /> Neue Vorlage
              </Button>
            </div>

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titel</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Zielgruppe</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Lade...</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Keine Vorlagen gefunden</TableCell></TableRow>
                  ) : (
                    filtered.map((t) => (
                      <TableRow key={t.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelected(t)}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            {t.title}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{TYPE_LABEL[t.instruction_type]}</TableCell>
                        <TableCell className="text-sm">{TARGET_GROUP_LABEL[t.target_group]}</TableCell>
                        <TableCell>v{t.version}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[t.status]}>{STATUS_LABEL[t.status]}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelected(t); }} className="gap-1">
                              <Eye className="h-4 w-4" /> Öffnen
                            </Button>
                            {isAdmin && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!confirm(`Vorlage „${t.title}" und alle zugehörigen Kategorien, Punkte, Fragen und Zuweisungen unwiderruflich löschen?`)) return;
                                  const { error } = await supabase
                                    .from("safety_instruction_templates")
                                    .delete()
                                    .eq("id", t.id);
                                  if (error) toast.error(error.message);
                                  else { toast.success("Vorlage gelöscht"); fetchTemplates(); }
                                }}
                                className="gap-1 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <SafetyAuditLog />
          </TabsContent>
        </Tabs>
      </CardContent>

      <SafetyTemplateEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editing}
        onSaved={() => { setEditorOpen(false); fetchTemplates(); }}
      />
    </Card>
  );
};

export default SafetyInstructionsSettings;
